import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import * as MuiIcons from "@mui/icons-material";
import { AddRounded } from "@mui/icons-material";
import * as Mui from "@mui/material";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { formatCurrency } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import { STATE_THRESHOLD_UPDATING } from "../constants/CashbackRecoil";
import { STATE_ACTIVE_FILTERS } from "../constants/ThresholdsConfigurationRecoil";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { useCashBackRepository } from "../../../api/repositories/CashbackRepository";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { useNavigate } from "react-router-dom";
import EditThreshold from "../sections/EditThreshold";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { convertTypology } from "../util/CashbackUtil";
import useToggle from "../hooks/useToggle";
import { useHasScope } from "../../../../../config/globalConfig";
import { CASHBACK_CONFIGURE_THRESHOLD } from "../../../utils/FinanceScope";

const ThresholdTable = () => {

    const UNIQUE_ID = "fnn-cashback-threshold";

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { t } = useTranslation('finance', { keyPrefix: 'cashback' });
    const { deleteThreshold, getThreshold } = useCashBackRepository();
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const [updating, setUpdating] = useRecoilState(STATE_THRESHOLD_UPDATING);
    const {  globalData: { user } } = useContext(GlobalContext);
    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");


    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);

    const [open, setOpen] = useState(false);
    const onClickCloseDialog = () => setOpen(false);
    const [threshold, setThreshold] = useState<any | undefined>(undefined);
    const [customerNameForDelete, setCustomerNameForDelete] = useState<any>();

    const [showEdit, toggleEdit] = useToggle();

    const gridRef = useRef<AgGridReact>(null);


    const columnDefs : ColDef[] = [
        {
            field: 'customer-name', headerName: t('tables.threshold.customer-name'), headerTooltip: t('tables.threshold.customer-name'),
            suppressSizeToFit: true,
            minWidth: 150,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true,
        },
        {
            field: 'threshold', headerName: t('tables.threshold.threshold-value'), headerTooltip: t('tables.printout.threshold-value'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: 'percentage', headerName: t('tables.threshold.percentage'), headerTooltip: t('tables.threshold.percentage'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => `${params.value}%`
        },
        {
            field: 'typology', headerName: t('tables.threshold.typology'), headerTooltip: t('tables.threshold.typology'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(params.value))
        },
        {
            field: "legalEntity", headerName: t("tables.threshold.legal-entity"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.threshold.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true,
            hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                function openEditModal({ data }: any): void {
                  setThreshold(params.data);
                  toggleEdit();
                }
              
                function onClickDelete(params: any): void {
                  setThreshold(params.data);
                  setCustomerNameForDelete(params.data['customer-name']);
                  setOpen(true);
                }
              
                return {
                  component: () => {
                    if (!hasScope(CASHBACK_CONFIGURE_THRESHOLD)) {
                      return null;
                    }
              
                    return (
                      <FinanceDynamicActions>
                        <Mui.Tooltip title={t("tables.threshold.edit")}>
                          <Mui.IconButton color="accent" onClick={() => openEditModal(params)}>
                            <MuiIcons.EditRounded />
                          </Mui.IconButton>
                        </Mui.Tooltip>
                        <Tooltip title={t("tables.threshold.delete")}>
                          <Mui.IconButton color='accent' onClick={() => onClickDelete(params)} >
                            <MuiIcons.DeleteRounded />
                          </Mui.IconButton>
                        </Tooltip>
                      </FinanceDynamicActions>
                    );
                  },
                };
              },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.threshold.no-rows.title'), content: t('tables.threshold.no-rows.content') }
    };



    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));

    }, [filterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating])


    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {

                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort.toUpperCase();

                getThreshold(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {

                        let result = response;
                        if (result['total-amount'] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result['total-amount']
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {

        renderActions: () => (
            <Stack>
                {hasScope(CASHBACK_CONFIGURE_THRESHOLD) &&
                    <Button fullWidth color={"accent"} variant="contained" component="label" onClick={() => {
                        navigate("/finance/cashback/threshold-wizard")
                    }} startIcon={<AddRounded />}>{t('buttons.add-threshold')}</Button>
                }
            </Stack>
        )
    }

    const deleteSelectedThreshold = () => {

        deleteThreshold(threshold.id).then(() => {
            setUpdating(true);
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('tables.threshold.snackbar.success-delete'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            setOpen(false);
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('tables.threshold.snackbar.error-delete'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
        setOpen(false);
    }

    return (
        <Mui.Box>

            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={'multiple'}
                noRowsOverlayComponent={noRowsOverlayComponent}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current?.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols
                        });
                    }
                }}
            />
            {showEdit &&
                <EditThreshold
                    open={showEdit}
                    onClose={toggleEdit}
                    threshold={threshold}
                />
            }
            <Mui.Dialog
                open={open}
                PaperProps={{
                    sx: {
                        minWidth: "600px !important",
                        minHeight: "194px",
                        borderRadius: "4px",
                    },
                }}
            >
                <Mui.DialogTitle>
                    <Mui.Grid
                        container
                        alignItems="center"
                        alignContent="center"
                        direction="row"
                    >
                        <Mui.Grid xs item>
                            <Mui.Stack direction="row" alignItems="center" gap={1}>
                                <MuiIcons.Info sx={{ color: '#C8017D' }} fontSize="small" />
                                <Box >{t("tables.threshold.deleteTitle")}</Box>
                            </Mui.Stack>
                        </Mui.Grid>
                        <Mui.Grid
                            xs
                            item
                            sx={{
                                justifyContent: "flex-end",
                                display: "flex",
                            }}
                        >
                            <MuiIcons.Close
                                color="disabled"
                                fontSize="small"
                                onClick={onClickCloseDialog}
                            />
                        </Mui.Grid>
                    </Mui.Grid>
                </Mui.DialogTitle>

                <Mui.DialogContent>
                    <Mui.DialogContentText>
                        {t("tables.threshold.deleteBodyMessage", { customerName: customerNameForDelete })}
                    </Mui.DialogContentText>
                </Mui.DialogContent>
                <Mui.DialogActions>
                    <Mui.Stack direction="row" spacing={2} p={1} mr={1}>
                        <Mui.Button
                            sx={{
                                maxWidth: "111px",
                                maxHeight: "48px",
                                minWidth: "111px",
                                minHeight: "48px",
                            }}
                            variant="outlined"
                            onClick={onClickCloseDialog}
                        >
                            <Mui.Typography
                                sx={{
                                    textTransform: "capitalize",
                                    fontWeight: 700,
                                    lineHeight: 2.25,
                                    color: "#C8017D",
                                }}
                                variant="subtitle1"
                            >
                                {t("tables.threshold.actionButtons.cancel")}
                            </Mui.Typography>
                        </Mui.Button>

                        <Mui.Button
                            sx={{
                                maxWidth: "181px",
                                maxHeight: "48px",
                                minWidth: "111px",
                                minHeight: "48px",
                            }}
                            variant="contained"
                            color="accent"
                            onClick={deleteSelectedThreshold}
                        >
                            <Mui.Typography
                                sx={{
                                    textTransform: "capitalize",
                                    fontWeight: 700,
                                    lineHeight: 2.25,
                                    color: 'white'
                                }}
                                variant="subtitle1"
                            >
                                {t("tables.threshold.actionButtons.confirm")}
                            </Mui.Typography>
                        </Mui.Button>
                    </Mui.Stack>
                </Mui.DialogActions>
            </Mui.Dialog>
        </Mui.Box>
    );
}


export default ThresholdTable;

